"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPass, setShowPass] = useState(false);
  const [sesionCerrada, setSesionCerrada] = useState(false);

  // Llegada desde /api/auth/logout tras un cierre forzoso (p. ej. cuenta desactivada)
  useEffect(() => {
    setSesionCerrada(new URLSearchParams(window.location.search).get("sesion") === "cerrada");
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const json = await res.json();
    setLoading(false);

    if (!res.ok) {
      setError(json.error || "Error al iniciar sesión");
      return;
    }

    router.push("/nueva-demanda");
    router.refresh();
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Panel izquierdo — identidad */}
      <div className="hidden lg:flex lg:w-[45%] relative flex-col items-center justify-center overflow-hidden bg-zinc-900 border-r border-zinc-800">
        {/* Patrón de fondo */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,#ffffff 0,#ffffff 1px,transparent 0,transparent 60px),repeating-linear-gradient(90deg,#ffffff 0,#ffffff 1px,transparent 0,transparent 60px)",
          }}
        />

        {/* Línea dorada vertical */}
        <div className="absolute left-0 inset-y-0 w-px bg-gradient-to-b from-transparent via-amber-500/40 to-transparent" />

        <div className="relative z-10 flex flex-col items-center text-center px-16 max-w-md">
          <ScalesIcon />

          <div className="mt-10 mb-3">
            <h1 className="text-5xl font-black tracking-[0.2em] text-white uppercase">
              SPARTA
            </h1>
            <h2 className="text-xl font-light tracking-[0.55em] text-amber-500 uppercase mt-1">
              LEGAL
            </h2>
          </div>

          <div className="w-12 h-px bg-amber-500/50 my-8" />

          <p className="text-zinc-400 text-sm leading-relaxed font-light">
            Plataforma de gestión jurídica para profesionales del derecho.
            Eficiencia, precisión y confidencialidad.
          </p>

          <div className="mt-16 w-full space-y-6">
            {[
              { num: "01", label: "Gestión de expedientes", desc: "Control total sobre sus casos" },
              { num: "02", label: "Documentación automatizada", desc: "Demandas generadas con IA" },
              { num: "03", label: "Seguridad garantizada", desc: "Datos cifrados y protegidos" },
            ].map((item) => (
              <div key={item.num} className="flex items-start gap-4 text-left">
                <span className="text-amber-500/40 font-mono text-xs mt-0.5 shrink-0">{item.num}</span>
                <div>
                  <p className="text-zinc-300 text-sm font-medium">{item.label}</p>
                  <p className="text-zinc-600 text-xs mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[380px]">
          {/* Logo móvil */}
          <div className="flex flex-col items-center mb-12 lg:hidden">
            <ScalesIcon size="sm" />
            <h1 className="mt-5 text-3xl font-black tracking-[0.2em] text-white uppercase">SPARTA</h1>
            <p className="text-amber-500 tracking-[0.5em] text-sm uppercase font-light mt-1">LEGAL</p>
          </div>

          <div className="mb-10">
            <h2 className="text-2xl font-bold text-white tracking-tight">Bienvenido</h2>
            <p className="text-zinc-500 text-sm mt-1.5">Introduzca sus credenciales de acceso</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField
              id="email"
              label="Correo electrónico"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="nombre@despacho.es"
              autoComplete="email"
              required
            />

            <div>
              <label htmlFor="password" className="block text-xs font-semibold text-zinc-400 uppercase tracking-widest mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  className="w-full bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-700 rounded-lg px-4 py-3 text-sm pr-11 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400 transition-colors"
                  tabIndex={-1}
                >
                  {showPass ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {sesionCerrada && !error && (
              <div className="flex items-center gap-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3">
                <AlertIcon />
                <p className="text-amber-400 text-sm">Tu sesión se ha cerrado. Vuelve a iniciar sesión.</p>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2.5 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                <AlertIcon />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-950 font-bold py-3.5 rounded-lg text-sm tracking-wide transition-all mt-2 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <SpinnerIcon />
                  Verificando...
                </>
              ) : (
                "Acceder al sistema"
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-zinc-600 text-sm">
              ¿No tiene cuenta?{" "}
              <Link href="/registro" className="text-amber-500 hover:text-amber-400 font-medium transition-colors">
                Crear cuenta
              </Link>
            </p>
          </div>

          <p className="mt-12 text-center text-zinc-700 text-xs">
            Acceso restringido · Personal autorizado
          </p>
        </div>
      </div>
    </div>
  );
}

function FormField({
  id, label, type, value, onChange, placeholder, autoComplete, required,
}: {
  id: string; label: string; type: string; value: string;
  onChange: (v: string) => void; placeholder: string;
  autoComplete?: string; required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-semibold text-zinc-400 uppercase tracking-widest mb-2">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        placeholder={placeholder}
        className="w-full bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-700 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-all"
      />
    </div>
  );
}

function ScalesIcon({ size = "lg" }: { size?: "sm" | "lg" }) {
  const d = size === "lg" ? 72 : 48;
  return (
    <svg width={d} height={d} viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Base */}
      <rect x="31" y="56" width="10" height="3" rx="1.5" fill="#d97706" opacity="0.8"/>
      <rect x="28" y="59" width="16" height="2.5" rx="1.25" fill="#d97706" opacity="0.6"/>
      {/* Palo central */}
      <rect x="35" y="14" width="2" height="42" rx="1" fill="#d97706" opacity="0.7"/>
      {/* Barra horizontal */}
      <rect x="12" y="22" width="48" height="2" rx="1" fill="#d97706"/>
      {/* Cadenas */}
      <line x1="20" y1="24" x2="20" y2="32" stroke="#d97706" strokeWidth="1.5" strokeDasharray="2 2"/>
      <line x1="52" y1="24" x2="52" y2="32" stroke="#d97706" strokeWidth="1.5" strokeDasharray="2 2"/>
      {/* Platillos */}
      <path d="M11 32 Q20 38 29 32" stroke="#d97706" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M43 32 Q52 38 61 32" stroke="#d97706" strokeWidth="2" fill="none" strokeLinecap="round"/>
      {/* Puntas de la barra */}
      <circle cx="12" cy="23" r="2" fill="#d97706"/>
      <circle cx="60" cy="23" r="2" fill="#d97706"/>
      {/* Esfera central */}
      <circle cx="36" cy="15" r="3" fill="#d97706"/>
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg className="w-4 h-4 text-red-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}
function SpinnerIcon() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
    </svg>
  );
}
